// Phase I3 — Match Command Header.
// Faithful port of prototype/super-acca.jsx::FixtureCommandHeader.
// Renders the fixture, kickoff countdown, league/matchweek meta, and the
// top-of-page sharp-money arrow. When `fixture` is null we still render
// the shell so the empty/abstain page has something visually anchored.

import type { FixtureMeta } from "../../../super-acca/useSuperAccaToday";
import type { SharpMoneyView } from "../../../super-acca/view-models";
import { Countdown } from "../../../components/super-acca";

function shortName(s: string | undefined | null): string {
  if (!s) return "—";
  const cleaned = s.replace(/[^A-Za-z\s]/g, "").trim().toUpperCase();
  if (cleaned.length <= 3) return cleaned.padEnd(3, "·").slice(0, 3);
  return cleaned.split(/\s+/)[0]!.slice(0, 3);
}

export interface MatchCommandHeaderProps {
  fixture: FixtureMeta | null;
  sharp: SharpMoneyView;
  publishedLabel?: string | null;
}

export function MatchCommandHeader({
  fixture,
  sharp,
  publishedLabel,
}: MatchCommandHeaderProps) {
  const home = fixture?.home ?? "—";
  const away = fixture?.away ?? "—";
  const homeShort = shortName(home);
  const awayShort = shortName(away);
  const league = fixture?.league ?? fixture?.competition ?? "Awaiting fixture";
  const kickoff = fixture?.kickoff_at ?? null;

  return (
    <div
      style={{
        position: "relative",
        padding: "36px 40px",
        background:
          "linear-gradient(180deg, var(--sa-bg-raised) 0%, var(--sa-bg-base) 100%)",
        borderBottom: "1px solid var(--sa-hairline)",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(900px 300px at 75% 0%, rgba(212,168,67,0.10), transparent 60%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
          <Crest text={homeShort} />
          <div>
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 10.5,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--sa-accent-saffron)",
              }}
            >
              {league.toUpperCase()}
            </div>
            <div
              style={{
                fontFamily: "var(--sa-font-stat)",
                fontSize: 64,
                lineHeight: 0.92,
                marginTop: 10,
                letterSpacing: "0.01em",
                color: "var(--sa-fg-primary)",
              }}
            >
              {home.toUpperCase()}{" "}
              <span style={{ color: "var(--sa-fg-muted)" }}>VS</span>{" "}
              {away.toUpperCase()}
            </div>
            {kickoff ? (
              <div
                style={{
                  display: "flex",
                  gap: 18,
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 11,
                  color: "var(--sa-fg-muted)",
                  letterSpacing: "0.14em",
                  marginTop: 12,
                }}
              >
                <span>◷ {fmtKickoff(kickoff)}</span>
                {sharp.now !== null && sharp.open !== null ? (
                  <span style={{ color: "var(--sa-accent-saffron)" }}>
                    ● Line {sharp.open.toFixed(2)} → {sharp.now.toFixed(2)}{" "}
                    {sharp.signal === "CONFIRMS" ? "↗" : sharp.signal === "CHALLENGES" ? "↘" : "·"}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          <Crest text={awayShort} />
        </div>
        <div style={{ textAlign: "right" }}>
          {kickoff ? (
            <>
              <div
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 10,
                  color: "var(--sa-accent-saffron)",
                  letterSpacing: "0.2em",
                }}
              >
                KICKOFF IN
              </div>
              <div style={{ marginTop: 8 }}>
                <Countdown targetISO={kickoff} />
              </div>
            </>
          ) : null}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginTop: 14,
              padding: "5px 10px",
              background: "var(--sa-signal-green-dim)",
              color: "var(--sa-signal-green)",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              letterSpacing: "0.18em",
              borderRadius: 2,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--sa-signal-green)",
              }}
            />
            {publishedLabel ?? "PUBLISHED · 11:30 GMT"}
          </div>
        </div>
      </div>
    </div>
  );
}

function Crest({ text }: { text: string }) {
  return (
    <div
      style={{
        width: 78,
        height: 78,
        background: "var(--sa-bg-panel)",
        border: "1px solid var(--sa-hairline-bright)",
        borderTop: "3px solid var(--sa-amber-dim)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--sa-font-stat)",
        fontSize: 30,
        color: "var(--sa-fg-primary)",
        letterSpacing: "0.06em",
        borderRadius: 4,
      }}
    >
      {text}
    </div>
  );
}

function fmtKickoff(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
